import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getRecipeById } from '../../../api/healthAPI';
import { errorToast, getLocalStorageItem } from '../../../utils/helper';

const ViewRecipe = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get recipe ID from URL params or location state
  const recipeId = searchParams.get('id') || location.state?._id;

  useEffect(() => {
    if (recipeId) {
      fetchRecipe();
    } else {
      errorToast('Recipe ID not found');
      navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipes`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await getRecipeById(recipeId);
      console.log('Recipe response:', response);

      if (response?.data) {
        setRecipe(response.data);
      } else {
        throw new Error('Recipe not found');
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      errorToast(error.message || 'Failed to fetch recipe');
      navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipes`);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/recipes`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Recipe not found</h2>
          <button
            onClick={handleBack}
            className="btn-primary mt-4"
          >
            Back to Meals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative lg:mb-[45px]">
      {/* Recipe Image with Edit Button */}
      <div className="mb-6">
        <div className="flex justify-end mb-3">
          <button
            onClick={() =>
              navigate(
                `${userData?.slug ? `/${userData.slug}` : ''}/health/recipe/new?id=${recipeId}`
              )
            }
            className="px-6 py-2 bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-full text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
          >
            Edit Recipe
          </button>
        </div>
        {recipe.image_url && (
          <div className="w-48 h-48 rounded-lg overflow-hidden">
            <img src={recipe.image_url} alt={recipe.title} className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      {/* Recipe Details Section */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Recipe Details</h2>

        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
          {/* Recipe Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipe Title
            </label>
            <input
              type="text"
              value={recipe.title}
              disabled
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed"
            />
          </div>

          {/* Recipe Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipe Category
            </label>
            <select
              value={recipe.category}
              disabled
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed appearance-none"
            >
              <option>{recipe.category}</option>
            </select>
          </div>

          {/* Serving */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Serving
            </label>
            <select
              value={recipe.servings}
              disabled
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed appearance-none"
            >
              <option>{recipe.servings}</option>
            </select>
          </div>

          {/* Cooking Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cooking Duration (Mins)
            </label>
            <select
              value={recipe.cooking_duration}
              disabled
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed appearance-none"
            >
              <option>{recipe.cooking_duration}</option>
            </select>
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Difficulty
            </label>
            <select
              value={recipe.difficulty}
              disabled
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed appearance-none"
            >
              <option>{recipe.difficulty}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Chef's Notes Section */}
      {recipe.notes && (
        <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Chef's Notes</h2>
          <textarea
            value={recipe.notes}
            disabled
            rows={4}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed resize-none"
          />
        </div>
      )}

      {/* Ingredients Section */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ingredients</h2>
        <div className="space-y-3">
          {recipe.ingredients && recipe.ingredients.length > 0 ? (
            recipe.ingredients.map((ingredient, index) => (
              <div key={ingredient._id || index} className="flex gap-3 items-center w-[65%]">
                <input
                  type="text"
                  value={ingredient.ingredient}
                  disabled
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed"
                />
                <input
                  type="text"
                  value={`${ingredient.amount} ${ingredient.unit}`}
                  disabled
                  className="w-32 px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed text-right"
                />
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No ingredients added</p>
          )}
        </div>
      </div>

      {/* Preparation Section */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preparation</h2>
        <div className="space-y-4">
          {recipe.preparation_steps && recipe.preparation_steps.length > 0 ? (
            recipe.preparation_steps.map((step, index) => (
              <div
                key={step._id || index}
                className="pb-4 border-b border-gray-200 dark:border-shoorah-darkBorder last:border-0 last:pb-0"
              >
                <div className="mb-2">
                  <span className="inline-block px-3 py-1 bg-gray-100 dark:bg-shoorah-darkBgColor text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                    Step {index + 1}
                  </span>
                </div>
                <p className="text-sm ml-2 text-gray-700 dark:text-gray-300 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No preparation steps added</p>
          )}
        </div>
      </div>

      {/* Nutritional Value Section */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Nutritional Value
        </h2>
        <div className="space-y-3">
          {recipe.nutritional_value && recipe.nutritional_value.length > 0 ? (
            recipe.nutritional_value.map((nutrition, index) => (
              <div key={nutrition._id || index} className="flex gap-3 items-center w-[65%]">
                <input
                  type="text"
                  value={nutrition.ingredient}
                  disabled
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed"
                />
                <input
                  type="text"
                  value={`${nutrition.amount} ${nutrition.unit}`}
                  disabled
                  className="w-32 px-4 py-2.5 bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-200 dark:border-shoorah-darkBorder rounded-lg text-sm text-gray-900 dark:text-white cursor-not-allowed text-right"
                />
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No nutritional information added
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewRecipe;
